import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { Box, Button } from '@mui/material'
import { useRef } from 'react'
import { useTranslation } from 'react-i18next'

import LiveStatusBadge from './LiveStatusBadge'
import { liveDashboardUrl, useLiveEmbed } from './useLiveEmbed'

const BAR_HEIGHT = 40

interface LiveDashboardModalContentProps {
  siteId: string
  dashboardId: string
  title: string
}

/**
 * Full-size live dashboard shown in the Dashboards modal, with its own handshake with the live app.
 * The badge and the "open in a tab" button sit in a bar just above the iframe so they hide nothing.
 */
const LiveDashboardModalContent = ({ siteId, dashboardId, title }: LiveDashboardModalContentProps) => {
  const { t } = useTranslation()
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const src = liveDashboardUrl(siteId, dashboardId, { embed: true })
  const state = useLiveEmbed(iframeRef, src)

  return (
    <Box sx={{ width: '90vw', display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ height: BAR_HEIGHT, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <LiveStatusBadge state={state} />
        <Button
          startIcon={<OpenInNewIcon />}
          href={liveDashboardUrl(siteId, dashboardId, { embed: false })}
          target="_blank"
          rel="noopener noreferrer"
          variant="contained"
          color="primary"
          sx={{ fontSize: '0.7rem' }}
        >
          {t('dashboards.live.openInNewTab')}
        </Button>
      </Box>
      <Box
        sx={{
          width: '100%',
          height: `min(calc(90vh - ${BAR_HEIGHT}px), calc(90vw * 9 / 16))`,
          backgroundColor: '#1e1e1e',
          borderRadius: '6px',
          overflow: 'hidden',
        }}
      >
        <iframe ref={iframeRef} src={src} title={title} style={{ width: '100%', height: '100%', border: 0 }} />
      </Box>
    </Box>
  )
}

export default LiveDashboardModalContent
